/**
 * AI Coach chat state hook. Extracted from `AICoachBubble.jsx` so the
 * component stays under the complexity ceiling and the state machine
 * can be reused (or tested) independently of the visual shell.
 *
 * Responsibilities
 *  • Loads history from `/ai-coach/history` when the chat opens
 *  • Sends messages via `/ai-coach/chat` with an optimistic user turn
 *  • Handles clear + escalation-to-human flows
 *  • Exposes derived value `lastEscalateMsgId` so the panel knows when
 *    to show the "Send this to Coach D" CTA
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';

export const useAICoach = (open) => {
  const { user, api } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [escalating, setEscalating] = useState(false);
  const [attachedImage, setAttachedImage] = useState(null); // { file, previewUrl }
  const [uploadingImage, setUploadingImage] = useState(false);
  const inputRef = useRef(null);

  // Load latest history whenever the panel opens.
  useEffect(() => {
    if (!open || !user) return;
    let cancelled = false;
    (async () => {
      setLoadingHistory(true);
      try {
        const res = await api().get('/ai-coach/history');
        if (!cancelled) setMessages(res.data.messages || []);
      } catch {
        // silent — first-time users get a fresh thread
      }
      if (!cancelled) {
        setLoadingHistory(false);
        setTimeout(() => inputRef.current?.focus(), 50);
      }
    })();
    return () => { cancelled = true; };
  }, [open, user, api]);

  const attachImage = useCallback((file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please pick an image file');
      return;
    }
    if (file.size > 25 * 1024 * 1024) {
      toast.error('Image is over 25 MB — try a smaller one');
      return;
    }
    setAttachedImage({ file, previewUrl: URL.createObjectURL(file) });
  }, []);

  const removeAttachedImage = useCallback(() => {
    setAttachedImage((prev) => {
      if (prev?.previewUrl) URL.revokeObjectURL(prev.previewUrl);
      return null;
    });
  }, []);

  const sendMessage = useCallback(async () => {
    const text = input.trim();
    if ((!text && !attachedImage) || sending || uploadingImage) return;

    // Upload image first (if any) so the AI can fetch it by URL.
    let uploadedUrl = null;
    if (attachedImage) {
      setUploadingImage(true);
      try {
        const form = new FormData();
        form.append('file', attachedImage.file);
        const up = await api().post('/ai-coach/upload-photo', form, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        uploadedUrl = up.data.image_url;
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Photo upload failed');
        setUploadingImage(false);
        return;
      }
      setUploadingImage(false);
    }

    setInput('');
    setSending(true);
    const optimistic = {
      id: `tmp-${Date.now()}`,
      role: 'user',
      content: text,
      image_url: uploadedUrl,
      created_at: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, optimistic]);
    // Clear preview now that the upload is committed.
    removeAttachedImage();
    try {
      const res = await api().post('/ai-coach/chat', {
        message: text,
        image_url: uploadedUrl,
      });
      setMessages((prev) => [
        ...prev,
        {
          id: res.data.message_id,
          role: 'assistant',
          content: res.data.reply,
          escalate: res.data.escalate,
          created_at: new Date().toISOString(),
        },
      ]);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Coach is offline, try again');
      setMessages((prev) => prev.filter((m) => m.id !== optimistic.id));
    }
    setSending(false);
    setTimeout(() => inputRef.current?.focus(), 30);
  }, [input, sending, uploadingImage, attachedImage, api, removeAttachedImage]);

  const escalate = useCallback(async () => {
    if (escalating) return;
    setEscalating(true);
    try {
      await api().post('/ai-coach/escalate', { note: '' });
      toast.success('Sent to Coach D — reply coming to your Messages inbox');
      const res = await api().get('/ai-coach/history');
      setMessages(res.data.messages || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not forward to Coach D');
    }
    setEscalating(false);
  }, [escalating, api]);

  const clearHistory = useCallback(async () => {
    if (!window.confirm('Clear your chat with the AI Coach?')) return;
    try {
      await api().delete('/ai-coach/history');
      setMessages([]);
      toast.success('Chat cleared');
    } catch {
      toast.error('Could not clear chat');
    }
  }, [api]);

  // Only the most recent assistant message with `escalate=true` gets the
  // CTA — showing one at a time keeps the UI focused.
  const lastEscalateMsgId = (() => {
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      const m = messages[i];
      if (m.role === 'assistant' && m.escalate) return m.id;
    }
    return null;
  })();

  return {
    user,
    messages,
    input,
    setInput,
    sending,
    loadingHistory,
    escalating,
    inputRef,
    lastEscalateMsgId,
    sendMessage,
    escalate,
    clearHistory,
    attachedImage,
    uploadingImage,
    attachImage,
    removeAttachedImage,
  };
};

export default useAICoach;
